import { cn } from '@invent/wl-ui-kit-next';
import {
  Dialog,
  DialogClose,
  DialogContent as KitDialogContent,
  DialogDescription,
  DialogFooter as KitDialogFooter,
  DialogHeader as KitDialogHeader,
  DialogTitle,
  DialogTrigger
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/**
 * The kit's `Dialog` with the house shape built in (SR-455). The kit's `DialogContent` carries no
 * padding and no width of its own: `DialogHeader` brings `px-6` and a rule under it,
 * `DialogFooter` `px-6 py-3` and a rule over it, and the body between them is the caller's to
 * pad. Every caller had to remember that, the Lists and Website dialogs did it by hand, and the
 * two Rankings dialogs did not — their fields ran into the frame and their buttons touched, which
 * is what the client reported. So the shape is here, once:
 *
 * - **`DialogContent`** is as wide as `max-w-lg` and never wider than the window less a margin;
 *   a caller's `className` still wins (`max-w-3xl`, the command palette's own frame).
 * - **`DialogHeader`** has room above and below its title and keeps clear of the close button.
 * - **`DialogBody`** is the padded middle — a grid, `gap-4`, `px-6 py-5` — whose one column is
 *   `grid-cols-1`, `minmax(0, 1fr)`: an `auto` column is as wide as its widest content's
 *   min-content, so one long thing inside — a cycle's name in a box, a file's name — pushed every
 *   field past the padding (a control still needs `min-w-0` to shrink inside a grid of its own).
 *   `as` makes it the dialog's `form` where the fields are submitted, or an `article` (a post's
 *   preview).
 * - **`DialogFooter`** spaces its buttons.
 *
 * `AlertDialog` is not wrapped: the kit pads it whole (`p-6`, `gap-4`), and every confirmation in
 * the app draws correctly on it. Only the classes are the house's — no role, name, `data-*` hook or
 * DOM order changes, so a restyle here stays style-only for the suites.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof KitDialogContent>,
  React.ComponentPropsWithoutRef<typeof KitDialogContent>
>(({ className, ...props }, ref) => (
  <KitDialogContent
    ref={ref}
    className={cn('w-[calc(100%-2rem)] max-w-lg', className)}
    {...props}
  />
));
DialogContent.displayName = 'DialogContent';

function DialogHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <KitDialogHeader className={cn('gap-1 py-4 pr-12', className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <KitDialogFooter className={cn('gap-2', className)} {...props} />;
}

type DialogBodyProps =
  | ({ as?: 'div' } & React.ComponentPropsWithoutRef<'div'>)
  | ({ as: 'form' } & React.ComponentPropsWithoutRef<'form'>)
  | ({ as: 'article' } & React.ComponentPropsWithoutRef<'article'>);

function DialogBody({ as = 'div', className, ...props }: DialogBodyProps) {
  const Element = as as React.ElementType;
  return (
    <Element
      data-slot="dialog-body"
      className={cn('grid grid-cols-1 gap-4 px-6 py-5', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
};
